import React, { useState } from 'react';
import { X, XCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { WithdrawalRequest } from '../../../types';
import { useRejectWithdrawal } from '../../../hooks/useApi';
import { getWithdrawalListLabel } from '../../../utils/withdrawalRequester';
import { formatWithdrawalDisplayAmount } from '../../../utils/portalUsdWithdrawalDisplay';
import { LoadingSpinner } from '../../ui/Loading';

interface RejectWithdrawalModalProps {
  withdrawal: WithdrawalRequest | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const parseErrorMessage = (error: any): string => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.message) {
    return error.message;
  }
  return 'An error occurred while rejecting withdrawal';
};

const RejectWithdrawalModal: React.FC<RejectWithdrawalModalProps> = ({
  withdrawal,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [reason, setReason] = useState('');
  const rejectWithdrawalMutation = useRejectWithdrawal();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!withdrawal) return;

    if (!reason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    try {
      await rejectWithdrawalMutation.mutateAsync({
        transactionId: withdrawal.transactionId,
        data: {
          reason: reason.trim(),
        },
      });
      toast.success('Withdrawal rejected successfully');
      onSuccess?.();
      onClose();
      setReason('');
    } catch (error: any) {
      console.error('Reject withdrawal error:', error);
      const errorMessage = parseErrorMessage(error);
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  if (!isOpen || !withdrawal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg font-semibold">Reject Withdrawal</CardTitle>
          <Button variant="ghost" size="sm" onClick={handleClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to reject this withdrawal request?
              </p>
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm">
                  <div className="font-medium">{withdrawal.transactionId}</div>
                  <div className="text-muted-foreground">
                    {getWithdrawalListLabel(withdrawal)}
                  </div>
                  <div className="font-medium text-red-600">
                    {formatWithdrawalDisplayAmount(withdrawal, { signed: true })}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="reason" className="block text-sm font-medium mb-2">
                Rejection Reason <span className="text-red-600">*</span>
              </label>
              <Input
                id="reason"
                type="text"
                placeholder="Enter rejection reason..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={1000}
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                {reason.length}/1000 characters
              </p>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={rejectWithdrawalMutation.isLoading}
              >
                {rejectWithdrawalMutation.isLoading ? (
                  <div className="flex items-center">
                    <LoadingSpinner />
                    <span className="ml-2">Rejecting...</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject Withdrawal
                  </div>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default RejectWithdrawalModal;
